import { Asset } from "./assets";
import { Collection } from "./collections";
import { Conversation } from "./conversations";
import { DealRoom } from "./dealRoom";
import { UtmCampaign } from "./link";
import { Users } from "./streamAccessControl";


export interface CreatedBy {
  firstName?: string;
  lastName?: string;
}

interface UserGroup {
  id: string;
  name: string;
  description: string | null;
  admin: boolean;
  createStream: boolean;
  users: null;
  createdBy: string | null;
  createdDate: number;
  modifiedDate: number | null;
  usersCount: number;
  icons: string | null;
}

export interface AssetIcon {
  id: string;
  type: string;
  full: string;
  thumbnail: string;
  key: string;
}

export interface CampaignItem {
  id: string;
  name: string;
  key: string;
  manualIndex: number;
  analytics?: Analytics;
}

export interface ShareItem {
  id: string;
  subject: string;
  icon?: {
    thumbnail?: string;
  };
  createdDate: string;
  uniqueEmailOpens: number;
  uniqueLinkClicks: number;
  totalTimeSpent: number | null;
  source: string;
}

export interface LeadsContentItem {
  id: string;
  name: string;
  icon?: AssetIcon;
  shares: number;
  leads: number;
  createdDate: string;
}

interface DealAsset {
  id: string;
  name: string;
  icon?: {
    thumbnail?: string;
  };
}

export interface DealItem {
  id: string;
  name: string;
  asset: DealAsset;
  createdDate: string;
  stage: string;
  amount: number;
}

interface LeadAsset {
  id: string;
  name: string;
  icon?: {
    thumbnail?: string;
  };
}

export interface LeadItem {
  id: string;
  firstName: string;
  lastName?: string;
  email: string;
  company?: string;
  createdDate: string;
  asset?: LeadAsset;
  utmCampaign: UtmCampaign
}

export interface SearchTagItem {
  key: string;
  id: string;
  manualIndex: number;
  assetCount: number;
  searchCount: number;
}


export interface SearchQueryItem {
  query: string;
  totalCount: number;
  manualIndex: number;
  id: string;
}

export interface AssetMetadata {
  content_length: number;
  content_type: string;
  etag: string;
  extension: string;
  gsuite_alternate_link: string;
  gsuite_web_view_url: string;
  md5_checksum: string;
  modified_date: number;
  original_name: string;
  pages: number;
  read_time: number;
  url: string;
  video_duration: number;
}

export interface AssetRecord {
  id: string;
  name: string;
  createdDate: number;
  modified_date: number;
  icon: AssetIcon;
  metadata: AssetMetadata;
  leads: number;
  opportunities: number;
  shares: number;
}

export interface CollectionItem {
  id: string;
  name: string;
  icon?: {
    thumbnail?: string;
  };
  createdBy?: CreatedBy;
  createdDate?: string;
  assetsCount?: number;
  modifiedDate?: string;
}

export interface DealRoomItem {
  dealRoomId: string;
  name: string;
  icon?: {
    thumbnail?: string;
  };
  createdBy?: CreatedBy;
  createdDate?: string;
  assetsCount?: number;
  modifiedDate?: string;
  totalPeopleInvolved? : number;
  totalInteractionsInvolved? : number
}


export interface Analytics {
  total: number;
  views: number;
  avgTimeSpent: number;
  shares: number;
  assets: number;
  users: number;
  collections: number;
  recipients: number;
  downloads: number;
  totalReactionsCount?: number;
  totalCommentsCount?: number;
  totalTimeSpent?: number;
}

export interface CampaignAnalytics {
  total: number;
  views: number;
  avgTimeSpent: number;
  shares: number;
  assets: number;
  users: number;
  collections: number;
  recipients: number;
  downloads: number;
  [key: string]: any;
}

export interface ConversationRecord {
  createdBy: Users;
  share: Conversation;
  activityDate: number | null;
  assetViewer: string;
  totalAssetsCount: number;
  totalRecipients: number;
}

export interface CollectionRecord {
  analytics: Analytics;
  collection: Collection;
}

export interface DealRoomRecord {
  analytics: Analytics;
  dealRoom: DealRoom;
}
export interface CampaignRecord {
  name: string;
  source: string | null;
  analytics: CampaignAnalytics;
  [key: string]: any;
}

export interface AccountAnalytics {
  total: number;
  views: number;
  avgTimeSpent: number;
  shares: number;
  assets: number;
  users: number;
  collections: number;
  recipients: number;
  downloads: number;
  [key: string]: any;
}

export interface ClosedDealItem {
  id: string;
  name: string;
  icon?: AssetIcon;
  shares: number;
  opportunities: number;
  createdDate: string;
}

export interface AccountRecord {
  company: string;
  analytics: AccountAnalytics;
  [key: string]: any;
}

export interface DiscoveryContentMatrixRange {
  lower: number;
  upper: number | null;
  count: number;
}

export interface FetchDiscoveryPopularAssetsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryPopularAssetsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: PopularAssetsItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryPopularAssetsErrorResponse {
  error: any;
}

export interface FetchReportSummaryNudgesRequest {
  nudgesType: string;
}

export interface ReportsSummaryItem {
  activeUser?: { activeUsers: number };
  assets?: { assets: number };
  contact?: { engagedContacts: number };
  deal?: { deals: number; dealValue: number };
  lead?: { leads: number };
  session?: { teamViews: number };
  share?: { shares: number };
}

export interface FetchReportSummaryNudgesResponse {
  response: {
    item: ReportsSummaryItem;
    loading: boolean;
    error: any;
  };
  nudgesType: string;
}

export interface FetchReportSummaryNudgesErrorResponse {
  error: any;
}

export interface FetchDiscoveryAssetsSharedRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryAssetsSharedResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: PopularAssetsItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryAssetsSharedErrorResponse {
  error: any;
}

export interface FetchDiscoveryTopUsersRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryTopUsersResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUsersItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryTopUsersErrorResponse {
  error: any;
}

export interface FetchDiscoveryTopSharesRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryTopSharesResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUsersItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryTopSharesErrorResponse {
  error: any;
}

export interface FetchDiscoveryTopUserGroupsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryTopUserGroupsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUserGroupItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryTopUserGroupsErrorResponse {
  error: any;
}

export interface FetchDiscoveryCollectionsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryCollectionsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: CollectionsCreatedItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryCollectionsErrorResponse {
  error: any;
}
export interface FetchDiscoveryCollectionsSharedRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryCollectionsSharedResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: CollectionsCreatedItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryCollectionsSharedErrorResponse {
  error: any;
}

export interface FetchDiscoverySearchQueriesRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoverySearchQueriesResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: SearchQueriesItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoverySearchQueriesErrorResponse {
  error: any;
}

export interface FetchDiscoverySearchTagsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoverySearchTagsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: SearchTagsItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoverySearchTagsErrorResponse {
  error: any;
}

export interface TopUserGroupItem {
  userGroup: UserGroup;
  analytics: Analytics;
}

export interface PopularAssetsItem {
  assets: Asset[];
  analytics: Analytics;
}

export interface TopUsersItem {
  users: Users;
  analytics: Analytics;
}

export interface CollectionsCreatedItem {
  collection: Collection;
  analytics: Analytics;
}

export interface SearchQueriesItem {
  query: string;
  totalCount: number;
}

export interface SearchTagsItem {
  key: string;
  searchCount: number;
  assersCount: number;
}

export interface FetchDiscoveryAssetViewsGraphReportRequest {
  filter: any;
}

export interface FetchDiscoveryAssetViewsGraphReportResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchDiscoveryAssetViewsGraphReportErrorResponse {
  error: any;
}

export interface FetchDiscoveryAssetsGraphReportRequest {
  filter: any;
}

export interface FetchDiscoveryAssetsGraphReportResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchDiscoveryAssetsGraphReportErrorResponse {
  error: any;
}

export interface FetchDiscoveryCollectionActivityGraphRequest {
  filter: any;
}

export interface FetchDiscoveryCollectionActivityGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchDiscoveryCollectionActivityGraphErrorResponse {
  error: any;
}

export interface FetchDiscoveryShareGraphReportRequest {
  filter: any;
}

export interface FetchDiscoveryShareGraphReportResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchDiscoveryShareGraphReportErrorResponse {
  error: any;
}

export interface FetchDiscoveryContentMatrixAssetsRequest {
  filter: any;
  page: number;
  limit: number;
}

export interface FetchDiscoveryContentMatrixAssetsResponse {
  assets: Asset[];
  analytics: Analytics;
}

export interface FetchDiscoveryContentMatrixAssetsErrorResponse {
  error: any;
}

export interface FetchDiscoveryContentMatrixRequest {
  filter: any;
}

export interface FetchDiscoveryContentMatrixResponse {
  low: DiscoveryContentMatrixRange;
  belowAverage: DiscoveryContentMatrixRange;
  average: DiscoveryContentMatrixRange;
  high: DiscoveryContentMatrixRange;
}

export interface FetchDiscoveryContentMatrixErrorResponse {
  error: any;
}

export interface FetchEngagementAttentionTimeGraphRequest {
  filter: any;
}

export interface FetchEngagementAttentionTimeGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchEngagementAttentionTimeGraphErrorResponse {
  error: any;
}

export interface FetchEngagmentUniqueViewersGraphRequest {
  filter: any;
}

export interface FetchEngagmentUniqueViewersGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchEngagmentUniqueViewersGraphErrorResponse {
  error: any;
}

export interface FetchEngagmentShareGraphRequest {
  filter: any;
}

export interface FetchEngagmentShareGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchEngagmentShareGraphErrorResponse {
  error: any;
}

export interface FetchEngagementAssetViewsGraphRequest {
  filter: any;
}

export interface FetchEngagementAssetViewsGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchEngagementAssetViewsGraphErrorResponse {
  error: any;
}

export interface FetchEngagmentPopularAssetsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagmentPopularAssetsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: PopularAssetsItem[];
  totalRecordsCount: number;
}

export interface FetchEngagmentPopularAssetsErrorResponse {
  error: any;
}

export interface FetchEngagmentTopUsersRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagmentTopUsersResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUsersItem[];
  totalRecordsCount: number;
}

export interface FetchEngagmentTopUsersErrorResponse {
  error: any;
}

export interface FetchEngagementTopAccountsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagementTopAccountsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: AccountRecord[];
  totalRecordsCount: number;
}

export interface FetchEngagementTopAccountsErrorResponse {
  error: any;
}

export interface FetchEngagementTopCampaignsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagementTopCampaignsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: CampaignRecord[];
  totalRecordsCount: number;
}

export interface FetchEngagementTopCampaignsErrorResponse {
  error: any;
}

export interface FetchEngagementTopCampaignsSourcesRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagementTopCampaignsSourcesResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: CampaignRecord[];
  totalRecordsCount: number;
}

export interface FetchEngagementTopCampaignsSourcesErrorResponse {
  error: any;
}

export interface FetchEnagagementShareReportRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEnagagementShareReportResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: ConversationRecord[];
  totalRecordsCount: number;
}

export interface FetchEnagagementShareReportErrorResponse {
  error: any;
}

export interface FetchEngagementCollectionReportRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagementCollectionReportResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: CollectionRecord[];
  totalRecordsCount: number;
}

export interface FetchEngagementCollectionReportErrorResponse {
  error: any;
}

export interface FetchPerformanceInteractionsGraphRequest {
  filter: any;
}

export interface FetchPerformanceInteractionsGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchPerformanceInteractionsGraphErrorResponse {
  error: any;
}

export interface FetchPerformanceLeadsGraphRequest {
  filter: any;
}

export interface FetchPerformanceLeadsGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchPerformanceLeadsGraphErrorResponse {
  error: any;
}

export interface FetchPerformanceDealsGraphRequest {
  filter: any;
}

export interface FetchPerformanceDealsGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchPerformanceDealsGraphErrorResponse {
  error: any;
}

export interface FetchPerformanceDealValueGraphRequest {
  filter: any;
}

export interface FetchPerformanceDealValueGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchPerformanceDealValueGraphErrorResponse {
  error: any;
}

export interface FetchPerformanceClosedDealGraphRequest {
  filter: any;
}

export interface FetchPerformanceClosedDealGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchPerformanceClosedDealGraphErrorResponse {
  error: any;
}

export interface FetchPerformanceClosedDealValueGraphRequest {
  filter: any;
}

export interface FetchPerformanceClosedDealValueGraphResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchPerformanceClosedDealValueGraphErrorResponse {
  error: any;
}

export interface FetchPerformanceTopContentLeadsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchPerformanceTopContentLeadsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: AssetRecord[];
  totalRecordsCount: number;
}

export interface FetchPerformanceTopContentLeadsErrorResponse {
  error: any;
}

export interface DealRoomsCreatedItem {
  dealRoom: DealRoom;
  analytics: Analytics;
}

export enum ReportTabs {
  discovery = "discovery",
  engagement = "engagement",
  performance = "performance",
  advocacy = "advocacy"
}

export enum DiscoveryTables {
  assetViews = "assetViews", 
  assets = "assets",
  discoveryMatrix = "discoveryMatrix",
  collectionActivity = "collectionActivity",
  conversationActivity = "conversationActivity",
  popularTeam = "popularTeam",
  topUsers = "topUsers",
  topSharers = "topSharers",
  topUserGroups = "topUserGroups",
  collectionsCreated = "collectionsCreated",
  collectionsShared = "collectionsShared",
  dealRoomsCreated = "dealRoomsCreated",
  dealRoomsShared = "dealRoomsShared",
  sharedAssets = "sharedAssets",
  searchedQueries = "searchedQueries",
  usedTags = "usedTags"
}

export enum EngagementTables {
  avgAttentionTime = "avgAttentionTime",
  uniqueViewers = "uniqueViewers",
  shares = "shares",
  assetViews = "assetViews",
  popularViewers = "popularViewers",
  topViewers = "topViewers",
  topAccounts = "topAccounts",
  campaignPerforming = "campaignPerforming",
  contentShared = "contentShared",
  conversationActivity = "conversationActivity",
  collectionActivity = "collectionActivity",
  dealRoomActivity = "dealRoomActivity"
}

export enum PerformanceTables {
  interactionContent = "interactionContent",
  leads = "leads",
  deals = "deals",
  dealValue = "dealValue",
  closedDeals = "closedDeals",
  closedDealValue = "closedDealValue",
  topGenerateLeads = "topGenerateLeads",
  topClosedDeals = "topClosedDeals"
}

export enum AdvocacyTables {
  timeSpent = "timeSpent",
  views = "views",
  shares = "shares",
  topSharers = "topSharers",
  topSharedAssets = "topSharedAssets",
  topEngagedAssets = "topEngagedAssets",
  topUserGroups = "topUserGroups"
}

// advocacy time spent
export interface FetchAdvocacyTimeSpentGraphReport {
  filter: any;
}

export interface FetchingAdvocacyTimeSpentGraphReportSucceeded {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchingAdvocacyTimeSpentGraphReportFailed {
  error: any;
}

// advocacy views
export interface FetchAdvocacyViewsGraphReportRequest {
  filter: any;
}

export interface FetchingAdvocacyViewsGraphReportSucceeded {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchingAdvocacyViewsGraphReportFailed {
  error: any;
}

// advocacy shares
export interface FetchAdvocacyShareGraphReportRequest {
  filter: any;
}

export interface FetchAdvocacyShareGraphReportResponse {
  key: string;
  value: number;
  cumulative: number;
}

export interface FetchAdvocacyShareGraphReportErrorResponse {
  error: any;
}

// advocacy total engagement
export interface FetchAdvocacyTotalEngagementRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchAdvocacyTotalEngagementResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUsersItem[];
  totalRecordsCount: number;
}

export interface FetchAdvocacyTotalEngagementErrorResponse {
  error: any;
}

// advocacy top shared assets
export interface FetchAdvocacyTopSharedAssetsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchAdvocacyTopSharedAssetsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: PopularAssetsItem[];
  totalRecordsCount: number;
}

export interface FetchAdvocacyTopSharedAssetsErrorResponse {
  error: any;
}

// advocacy top engaged assets
export interface FetchAdvocacyTopEngagedAssetsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchAdvocacyTopEngagedAssetsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: PopularAssetsItem[];
  totalRecordsCount: number;
}

export interface FetchAdvocacyTopEngagedAssetsErrorResponse {
  error: any;
}

// advocacy top sharers
export interface FetchAdvocacyTopSharersRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchAdvocacyTopSharersResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUsersItem[];
  totalRecordsCount: number;
}

export interface FetchAdvocacyTopSharersErrorResponse {
  error: any;
}

// advocacy top user groups
export interface FetchAdvocacyTopUserGroupsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchAdvocacyTopUserGroupsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: TopUserGroupItem[];
  totalRecordsCount: number;
}

export interface FetchAdvocacyTopUserGroupsErrorResponse {
  error: any;
}

export interface DealRoomsCreatedItem {
  dealRoom: DealRoom;
  analytics: Analytics;
}

export interface FetchDiscoveryDealRoomsRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchDiscoveryDealRoomsResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomsCreatedItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryDealRoomsErrorResponse {
  error: any;
}

export interface FetchDiscoveryDealRoomsSharedRequest {
  page: number;
  limit: number;
  filter: any;
}
export interface FetchDiscoveryDealRoomsSharedResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomsCreatedItem[];
  totalRecordsCount: number;
}

export interface FetchDiscoveryDealRoomsSharedErrorResponse {
  error: any;
}

export interface FetchEngagementDealRoomReportRequest {
  page: number;
  limit: number;
  filter: any;
}

export interface FetchEngagementDealRoomReportResponse {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  records: DealRoomRecord[];
  totalRecordsCount: number;
}

export interface FetchEngagementDealRoomReportErrorResponse {
  error: any;
}